import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  Button,
  Header,
  P,
  VersionHistoryTable,
  IVersion,
  VersionHistoryTableLabels,
} from '@salecycle/ui-component-library';
import { getTemplateVersionHistory } from 'sections/design/template-management/sections/template-editor/utils/queries';
import { ITemplateHistoryItem } from 'entities/template/template';
import useNotifications from 'core/notifications/useNotifications';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { ConfigHeaderWrapper, ConfigScrollContainer } from './style';
import { RcsTemplateEditorSaveUnsavedChangesModal } from './modals/RcsTemplateEditorSaveUnsavedChangesModal';

export const RcsVersionHistoryView: React.FC = () => {
  const { t } = useTranslation(['designs']);
  const { siteId, id: templateId, language } = useParams<{ siteId: string; id: string; language: string }>();
  const { hasUnsavedChanges, previewingVersion } = useRcsTemplateEditorState();
  const dispatch = useRcsTemplateEditorDispatch();
  const { showNotification } = useNotifications();

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingPreviewVersionId, setPendingPreviewVersionId] = useState<string | null>(null);

  const { isLoading, isSuccess, data, isIdle, refetch } = useQuery(
    ['templateHistory', siteId, templateId, language],
    () => getTemplateVersionHistory({ siteId, templateId, language, t }),
    {
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  React.useEffect(() => {
    if (!saveDialogOpen) {
      setPendingPreviewVersionId(null);
    }
  }, [saveDialogOpen]);

  React.useEffect(() => {
    if (isIdle || isLoading) {
      return;
    }
    if (!isSuccess) {
      showNotification(t('designs:designGallery.templateEditor.tabBar.notifications.templateHistoryError'), 'error');
      return;
    }
    if (data && data.length === 0) {
      showNotification(t('designs:designGallery.templateEditor.tabBar.notifications.noTemplateHistory'), 'error');
    }
  }, [isLoading, isSuccess, isIdle, data, showNotification, t]);

  const previewVersion = useCallback(
    (versionId: string) => {
      const setVersion = data && data.length > 0 && data[0].versionId === versionId ? '' : versionId;
      dispatch({
        type: 'SET_TEMPLATE_VERSION',
        payload: { versionId: setVersion },
      });
    },
    [data, dispatch],
  );

  const handlePreview = (version: IVersion) => {
    if (hasUnsavedChanges) {
      setPendingPreviewVersionId(version.id);
      setSaveDialogOpen(true);
      return;
    }
    previewVersion(version.id);
  };

  const versions: IVersion[] = useMemo(
    () =>
      (data ?? []).map((item: ITemplateHistoryItem) => ({
        id: item.versionId,
        date: item.date,
        authorName: item.name,
        comment: item.comment,
        authorEmail: item.email,
        authorId: item.userId,
      })),
    [data],
  );

  const labels: VersionHistoryTableLabels = useMemo(
    () => ({
      lastUpdated: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.lastUpdated'),
      updatedBy: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.updatedBy'),
      comment: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.comment'),
      preview: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.preview'),
      currentVersionBadge: t('designs:designGallery.templateEditor.versionHistory.table.label.currentVersionBadge'),
      selected: t('designs:designGallery.templateEditor.versionHistory.table.label.statusSelected'),
    }),
    [t],
  );

  const currentVersionId = data?.[0]?.versionId;
  const hasHistory = Boolean(data && data.length > 0);

  const handleApplyUnsavedChangeAction = useCallback(
    (notificationMessage: string) => {
      showNotification(notificationMessage, 'normal');
      refetch();
      if (pendingPreviewVersionId) {
        previewVersion(pendingPreviewVersionId);
        setPendingPreviewVersionId(null);
      }
    },
    [pendingPreviewVersionId, refetch, previewVersion, showNotification],
  );

  return (
    <>
      <ConfigScrollContainer data-testid="RcsVersionHistoryView">
        <ConfigHeaderWrapper>
          <Header
            heading={t('designs:rcsTemplateEditor.versionHistoryTab.header')}
            description={t('designs:rcsTemplateEditor.versionHistoryTab.description')}
          />
        </ConfigHeaderWrapper>
        {hasHistory && (
          <VersionHistoryTable
            versions={versions}
            selectedVersionId={previewingVersion}
            currentVersionId={currentVersionId}
            onPreview={handlePreview}
            labels={labels}
          />
        )}
        {!hasHistory && isLoading && (
          <P data-testid="loadingIndicator">
            {t('designs:designGallery.templateEditor.versionHistory.table.label.statusLoading')}
          </P>
        )}
        {!hasHistory && !isLoading && (
          <>
            <P data-testid="notAvailableIndicator">
              {t('designs:designGallery.templateEditor.versionHistory.table.label.statusNotAvailable')}
            </P>
            <P>
              <Button variation="secondary" onClick={() => refetch()}>
                {t('designs:designGallery.templateEditor.versionHistory.table.label.buttonReload')}
              </Button>
            </P>
          </>
        )}
      </ConfigScrollContainer>
      <RcsTemplateEditorSaveUnsavedChangesModal
        modalOpen={saveDialogOpen}
        setModalOpen={setSaveDialogOpen}
        onSave={() =>
          handleApplyUnsavedChangeAction(t('designs:designGallery.templateEditor.modals.unsavedChanges.saveSuccess'))
        }
        onDiscard={() =>
          handleApplyUnsavedChangeAction(t('designs:designGallery.templateEditor.modals.unsavedChanges.discardSuccess'))
        }
      />
    </>
  );
};
